/**
 * QuizRenderer - Unified quiz type → component dispatcher
 *
 * Supports multi-mode rendering: adult (default), student, print.
 * Mode is determined by: explicit prop > QuizModeContext > default ('adult').
 */

import { useTranslation } from 'react-i18next'
import type { QuizSolveResult } from '../../types/legacyQuiz'
import { useQuizMode, type QuizMode } from './quiz-mode-context'

import {
  BooleanAdult,
  MultiChoiceAdult,
  FillBlankAdult,
  SelectTextAdult,
  DragTextAdult,
  ColoringAdult,
  CountingAdult,
  ImageSelectAdult,
  OrderingAdult,
  PairingAdult,
  ExplanationAdult,
  InlineAdult,
} from './adult'

import {
  BooleanStudent,
  MultiChoiceStudent,
  FillBlankStudent,
  SelectTextStudent,
  DragTextStudent,
  ColoringStudent,
  CountingStudent,
  ImageSelectStudent,
  OrderingStudent,
  PairingStudent,
  ExplanationStudent,
  InlineStudent,
} from './student'

import {
  BooleanPrint,
  MultiChoicePrint,
  FillBlankPrint,
  SelectTextPrint,
  DragTextPrint,
  ColoringPrint,
  CountingPrint,
  ImageSelectPrint,
  OrderingPrint,
  PairingPrint,
  ExplanationPrint,
  InlinePrint,
} from './print'


type QuizComponent = React.ComponentType<any>

const ADULT_MAP: Record<string, QuizComponent> = {
  multi_choice: MultiChoiceAdult,
  boolean: BooleanAdult,
  fill_blank: FillBlankAdult,
  short_answer: FillBlankAdult,
  select_text: SelectTextAdult,
  drag_text: DragTextAdult,
  coloring: ColoringAdult,
  counting: CountingAdult,
  image_select: ImageSelectAdult,
  ordering: OrderingAdult,
  pairing: PairingAdult,
  explanation: ExplanationAdult,
  inline: InlineAdult,
}

const STUDENT_MAP: Record<string, QuizComponent> = {
  multi_choice: MultiChoiceStudent,
  boolean: BooleanStudent,
  fill_blank: FillBlankStudent,
  short_answer: FillBlankStudent,
  select_text: SelectTextStudent,
  drag_text: DragTextStudent,
  coloring: ColoringStudent,
  counting: CountingStudent,
  image_select: ImageSelectStudent,
  ordering: OrderingStudent,
  pairing: PairingStudent,
  explanation: ExplanationStudent,
  inline: InlineStudent,
}

const PRINT_MAP: Record<string, QuizComponent> = {
  multi_choice: MultiChoicePrint,
  boolean: BooleanPrint,
  fill_blank: FillBlankPrint,
  short_answer: FillBlankPrint,
  select_text: SelectTextPrint,
  drag_text: DragTextPrint,
  coloring: ColoringPrint,
  counting: CountingPrint,
  image_select: ImageSelectPrint,
  ordering: OrderingPrint,
  pairing: PairingPrint,
  explanation: ExplanationPrint,
  inline: InlinePrint,
}

const COMPONENT_MAP: Record<QuizMode, Record<string, QuizComponent>> = {
  adult: ADULT_MAP,
  student: STUDENT_MAP,
  print: PRINT_MAP,
}

export interface QuizRendererProps {
  quizType: string
  quizId: string | number

  quiz: any
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  showCorrectAnswers?: boolean
  /** pairing/image_select print reveal용 정답 키 배열 (...rest로 print 컴포넌트에 전달) */
  correctAnswers?: string[]
  /** drag_text 빈 박스 문구. 특정 호스트 화면에서만 기본 번역을 덮어쓸 때 사용한다. */
  blankPlaceholder?: string
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
  onAnswerChange?: (answers: string[]) => void
  disableSound?: boolean
  difficultyLevel?: number | null
  // Print mode: quiz question number (1-based)
  number?: number
  // FillBlankQuiz-specific
  useKeypad?: boolean
  // Mode override (takes precedence over context)
  mode?: QuizMode
  // Compact print layout
  compact?: boolean
  // G3 — true면 풀이 후 해설을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출(b2c/company back-compat).
  hideExplanation?: boolean
  /** true면 결과 토글은 유지하되 풀이/해설 UI만 숨긴다. */
  hideSolveDescription?: boolean
  /** 서술형의 자동채점 대기 inline 안내를 호스트가 별도 결과 UI로 대체할 때 숨긴다. */
  hideResultFeedback?: boolean
  /**
   * 서술형 복습에서 답 입력 없이 모범답안만 확인하는 모드.
   * explanation student renderer만 반응하며 다른 문항 유형에는 영향이 없다.
   */
  answerRevealOnly?: boolean
  /**
   * G4 — true면 선택지 '텍스트'만 opacity 0.05로 흐린다(번호·히트영역·이미지·선택 강조 유지).
   * multi_choice·inline 렌더러만 반응하고, ordering·pairing·drag_text·select_text·boolean 등은 무시한다.
   * LC QuizRunner에서 1회차 한정 주입. 기본 false=현행 노출(b2c/company/AIQ back-compat).
   */
  dimChoiceText?: boolean
}

export function QuizRenderer({ quizType, quiz, useKeypad, mode: modeProp, ...rest }: QuizRendererProps) {
  const { t } = useTranslation('quiz')
  const { mode: contextMode } = useQuizMode()
  const mode = modeProp ?? contextMode // prop > context > default('adult')

  const modeMap = COMPONENT_MAP[mode]
  const Component = modeMap?.[quizType]

  // Fallback to adult mode if component not found in current mode
  if (!Component) {
    const FallbackComponent = ADULT_MAP[quizType]
    if (FallbackComponent) {
      return <FallbackComponent quiz={quiz} useKeypad={useKeypad} {...rest} />
    }

    return (
      <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
        <p className="text-yellow-800">
          {t('container.unsupportedType', { type: quizType })}
        </p>
      </div>
    )
  }

  return <Component quiz={quiz} useKeypad={useKeypad} {...rest} />
}
